import { useEffect, useState } from 'react'

// Static cache to store IP address in memory (per module instance)
let cachedIP: string | null = null
const STORAGE_KEY = 'ip_address'

// Function to get IP address as a Promise (module + sessionStorage hybrid)
export const getIPAddress = async () => {
  try {
    // 1) In-memory cache
    if (cachedIP) {
      return cachedIP
    }

    // 2) sessionStorage (synchronous, fast)
    if (typeof window !== 'undefined') {
      const stored = sessionStorage.getItem(STORAGE_KEY)
      if (stored) {
        cachedIP = stored
        return stored
      }
    }

    // 3) Fetch from external services (only once per cold load)
    const ipServices = [
      'https://api.ipify.org?format=json',
      'https://httpbin.org/ip',
      'https://api.my-ip.io/ip.json',
      'https://ipapi.co/json/',
    ]

    for (const service of ipServices) {
      try {
        const response = await fetch(service)
        const data = await response.json()

        let ip = null
        if (data.ip) ip = data.ip
        else if (data.origin) ip = data.origin
        else if (data.query) ip = data.query

        if (ip) {
          // cache in-memory
          cachedIP = ip
          // cache in sessionStorage
          if (typeof window !== 'undefined') {
            try {
              sessionStorage.setItem(STORAGE_KEY, ip)
            } catch {}
          }
          return ip
        }
      } catch {
        // continue to next service
      }
    }

    return null
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error getting IP address:', error)
    return null
  }
}

// Custom Hook for IP Address
export const useIPAddress = () => {
  const [ipAddress, setIpAddress] = useState(null)

  useEffect(() => {
    let canceled = false
    getIPAddress()
      .then((ip) => {
        if (!canceled) setIpAddress(ip)
      })
      .catch(() => {
        if (!canceled) setIpAddress(null)
      })
    return () => {
      canceled = true
    }
  }, [])

  return ipAddress
}
